import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, ThumbsUp } from "lucide-react";
import { api } from "@/lib/api";
import KeepersGoal from "@/pages/worth/KeepersGoal";
import SendKeepers from "@/pages/worth/SendKeepers";

const tz = () => new Date().getTimezoneOffset();
const usd = (v) => `$${Math.round(v).toLocaleString()}`;
export const keepersChanged = () => window.dispatchEvent(new Event("snaplist:keepers"));

// Today's Keepers batch with its running value, plus one tap to thumbs-up every keeper still left on this list.
export default function KeepersBar({ targets, onFinish, testid = "keepers-bar" }) {
  const [today, setToday] = useState(null);
  const [prog, setProg] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/keepers/today", { params: { tz_offset: tz() } }).then((r) => setToday(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("snaplist:keepers", load);
    return () => window.removeEventListener("snaplist:keepers", load);
  }, [load]);

  const keepAll = async () => {
    let done = 0;
    setProg(0);
    for (const t of targets) { // one at a time so they all land in the same batch
      try {
        const { data } = await api.post(t.path, { tz_offset: tz() });
        t.onDone?.(data);
        done += 1;
        setProg(done);
        setToday((t) => ({ ...t, batch_id: data.batch_id, name: data.batch_name, count: data.today_count, value: data.today_value }));
      } catch { /* skip and keep going */ }
    }
    setProg(null);
    load();
    onFinish?.();
    toast.success(`${done} keeper${done === 1 ? "" : "s"} added to Keepers`);
  };

  const goal = today?.goal || 0;
  const pct = goal ? Math.min(100, Math.round(((today?.value || 0) / goal) * 100)) : 100;
  useEffect(() => { // one cheer a day when the goal is hit
    if (!goal || (today?.value || 0) < goal) return;
    const day = new Date().toDateString();
    if (localStorage.getItem("snaplist.keepersGoalHit") === day) return;
    localStorage.setItem("snaplist.keepersGoalHit", day);
    toast.success(`Daily goal hit! ${usd(today.value)} in keepers today`);
  }, [goal, today?.value]);

  if (!targets.length && !today?.count && !goal) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {targets.length > 0 && (
        <button type="button" onClick={keepAll} disabled={prog !== null}
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-70"
          data-testid={`${testid}-all`}>
          <ThumbsUp className="h-3.5 w-3.5" />
          {prog !== null ? `Adding ${prog}/${targets.length}…` : `Thumbs up all ${targets.length} keeper${targets.length === 1 ? "" : "s"}`}
        </button>
      )}
      {(today?.count > 0 || goal > 0) && (
        <button type="button" onClick={() => today?.batch_id && navigate(`/batch?b=${today.batch_id}`)}
          style={{ background: `linear-gradient(90deg, #34d399 ${pct}%, #d1fae5 ${pct}%)`, transition: "background 0.6s ease-out" }}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-transform active:scale-95 text-emerald-950 ring-1 ring-emerald-500`}
          data-testid={`${testid}-open`}>
          <ThumbsUp className="h-3.5 w-3.5" fill="currentColor" />
          Keepers today · <span data-testid={`${testid}-count`}>{today?.count || 0}</span> · <span className="font-mono" data-testid={`${testid}-value`}>{usd(today?.value || 0)}</span>
          {goal > 0 && <span className="font-mono opacity-80" data-testid={`${testid}-goal`}>/ {usd(goal)}</span>}
        </button>
      )}
      {today && <KeepersGoal goal={goal} onSaved={(g) => { setToday((t) => ({ ...t, goal: g })); load(); }} testid={testid} />}
      {goal > 0 && today?.streak > 0 && (
        <span title={`${today.streak} day${today.streak === 1 ? "" : "s"} in a row hitting your keepers goal${today.hit_today ? "" : ". Hit it today to keep it going"}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold ${today.hit_today ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-600"}`}
          data-testid={`${testid}-streak`}>
          <Flame className={`h-3.5 w-3.5 ${today.hit_today ? "text-orange-500" : "text-slate-400"}`} fill={today.hit_today ? "currentColor" : "none"} />
          <span data-testid={`${testid}-streak-days`}>{`${today.streak}-day streak`}</span>
        </span>
      )}
      {today?.count > 0 && <SendKeepers batchIds={today.batch_ids} count={today.count} testid={testid} />}
    </div>
  );
}
